import {createAsyncThunk} from "@reduxjs/toolkit";
import {endOfDay, getUnixTime, startOfDay} from "date-fns";
import {getData} from "../../Api/api";
import {
    EP_LOCATION_INSIGHTS_SALES,
    EP_LOCATION_SALES_INFLUENCERS,
    EP_LOCATION_SALES_SOCIAL,
    EP_LOCATION_STATS
} from "../../Api/endPoints";
import {continentsChart} from "./localizations.mapper";
import { zonedTimeToUtc } from "date-fns-tz";

const buildBaseParams = ({ startDate, endDate, brand, skip = 0, limit = 10, order_by, sort = "DESC" }) => ({
    start: getUnixTime(zonedTimeToUtc(startOfDay(startDate),"GMT")),
    end: getUnixTime(zonedTimeToUtc(endOfDay(endDate),"GMT")),
    brand_id: brand,
    count: limit,
    offset: skip,
    ...(order_by ? { order_by } : {}),
    sort,
    timezone: new Date().getTimezoneOffset()
});

export const getLocationStats = createAsyncThunk(
    "location/getLocationStats",
    async ({startDate, endDate, brand = 0}, thunkAPI) => {
        try {
            const params = {
                start: getUnixTime(zonedTimeToUtc(startOfDay(startDate),"GMT")),
                end: getUnixTime(zonedTimeToUtc(endOfDay(endDate),"GMT")),
                brand_id: brand,
            };

            const response = await getData(EP_LOCATION_STATS, {params});
            const {continent_sales_per_period: continentSalesPerPeriod} = response;
            const rawSalesPerContinent =
                response?.sales_per_continent ?? response?.sales_per_continents ?? {};
            const salesPerContinent = Object.fromEntries(
                Object.entries(rawSalesPerContinent).filter(([key]) => key && key.trim() !== "")
            );

            return {
                salesPerContinent,
                charts: {continents: continentsChart(continentSalesPerPeriod, startDate)}
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);

export const getLocationCountrySales = createAsyncThunk(
    "location/getLocationCountrySales",
    async ({startDate, endDate, brand = 0, skip = 0, limit = 10, order_by = "total_sales", sort = "DESC"}, thunkAPI) => {
        try {
            const params = buildBaseParams({ startDate, endDate, brand, skip, limit, order_by, sort });
            const {
                sales_per_country: salesPerCountry = [],
                sales_per_continent: salesPerContinent = {},
                total_count: totalCount = 0
            } = await getData(EP_LOCATION_INSIGHTS_SALES, {params});

            return {
                salesPerCountry,
                salesPerContinent,
                salesPerCountryPerContinent: salesPerCountry,
                countrySalesTotalRows: Number(totalCount) || salesPerCountry.length || 0,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);

export const getLocationInfluencerSales = createAsyncThunk(
    "location/getLocationInfluencerSales",
    async ({startDate, endDate, brand = 0, country = "FR", skip = 0, limit = 10, order_by = "total", sort = "DESC"}, thunkAPI) => {
        try {
            const params = {
                ...buildBaseParams({ startDate, endDate, brand, skip, limit, order_by, sort }),
                country
            };
            const {
                influencers_sales_per_country: rows = [],
                total_count: totalCount = 0
            } = await getData(EP_LOCATION_SALES_INFLUENCERS, {params});

            return {
                influencersSalesPerCountry: rows?.filter(value => Object.keys(value).length !== 0) ?? [],
                influencerSalesTotalRows: Number(totalCount) || rows.length || 0,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);

export const getLocationSocialSales = createAsyncThunk(
    "location/getLocationSocialSales",
    async ({startDate, endDate, brand = 0, country = "FR", skip = 0, limit = 10, order_by = "sales_nbr", sort = "DESC"}, thunkAPI) => {
        try {
            const params = {
                ...buildBaseParams({ startDate, endDate, brand, skip, limit, order_by, sort }),
                country
            };
            const {
                sm_sales_per_country: rows = [],
                total_count: totalCount = 0
            } = await getData(EP_LOCATION_SALES_SOCIAL, {params});
            return {
                smSalesPerCountry: rows,
                socialSalesTotalRows: Number(totalCount) || rows.length || 0,
            };
        } catch (error) {
            return thunkAPI.rejectWithValue(error?.message);
        }
    }
);
